import React, { useEffect, useState } from 'react';
import {
  Container, Card, Table, Button, Modal, Form, Row, Col, Badge, Spinner, Alert, InputGroup
} from 'react-bootstrap';
import Header from '../layouts/Header';
import axiosInstance from '../services/axiosInterceptor';

const CATEGORY_LABELS = {
  tuition: 'ค่าเทอม',
  utility: 'ค่าสาธารณูปโภค',
  exam: 'ค่าสอบ/เอกสาร',
  dormitory: 'ค่าหอพัก',
  other: 'อื่นๆ'
};

const emptyForm = {
  item_code: '',
  item_name: '',
  category: 'tuition',
  default_amount: '',
  is_active: 1,
  item_note: ''
};

const FeeItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get('/api/fee/items', { params: { search } });
      if (res.data.success) setItems(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดรายการค่าใช้จ่ายไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      item_code: item.item_code,
      item_name: item.item_name,
      category: item.category,
      default_amount: item.default_amount,
      is_active: item.is_active,
      item_note: item.item_note || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.item_code.trim() || !form.item_name.trim()) {
      setError('กรุณากรอกรหัสและชื่อรายการ');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, default_amount: Number(form.default_amount) || 0 };
      if (editing) {
        await axiosInstance.put(`/api/fee/items/${editing.item_id}`, payload);
      } else {
        await axiosInstance.post('/api/fee/items', payload);
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <Container fluid className="px-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h2 className="mb-1">รายการค่าใช้จ่าย</h2>
              <p className="text-muted mb-0">แคตตาล็อกรายการที่ใช้ตั้งราคาและออกบิล</p>
            </div>
            <Button variant="primary" onClick={openCreate}>
              <i className="ri-add-line me-1" /> เพิ่มรายการ
            </Button>
          </div>

          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>
          )}

          <Card className="border-0 shadow-sm">
            <Card.Body>
              <InputGroup className="mb-3" style={{ maxWidth: 360 }}>
                <Form.Control
                  placeholder="ค้นหารหัส/ชื่อรายการ"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchItems()}
                />
                <Button variant="outline-secondary" onClick={fetchItems}>
                  <i className="ri-search-line" />
                </Button>
              </InputGroup>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>รหัส</th>
                      <th>ชื่อรายการ</th>
                      <th>หมวด</th>
                      <th className="text-end">ราคาตั้งต้น</th>
                      <th className="text-center">สถานะ</th>
                      <th className="text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr><td colSpan={6} className="text-center text-muted py-4">ยังไม่มีรายการ</td></tr>
                    ) : (
                      items.map((item) => (
                        <tr key={item.item_id}>
                          <td>{item.item_code}</td>
                          <td>{item.item_name}</td>
                          <td>{CATEGORY_LABELS[item.category] || item.category}</td>
                          <td className="text-end">{Number(item.default_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
                          <td className="text-center">
                            {item.is_active
                              ? <Badge bg="success">ใช้งาน</Badge>
                              : <Badge bg="secondary">ปิด</Badge>}
                          </td>
                          <td className="text-center">
                            <Button size="sm" variant="outline-primary" onClick={() => openEdit(item)}>
                              <i className="ri-pencil-line" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </main>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'แก้ไขรายการ' : 'เพิ่มรายการค่าใช้จ่าย'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={5}>
              <Form.Label>รหัสรายการ</Form.Label>
              <Form.Control
                value={form.item_code}
                onChange={(e) => setForm({ ...form, item_code: e.target.value })}
                placeholder="เช่น TUITION"
              />
            </Col>
            <Col xs={7}>
              <Form.Label>ชื่อรายการ</Form.Label>
              <Form.Control
                value={form.item_name}
                onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                placeholder="เช่น ค่าเทอม"
              />
            </Col>
            <Col xs={6}>
              <Form.Label>หมวด</Form.Label>
              <Form.Select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6}>
              <Form.Label>ราคาตั้งต้น (บาท)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={form.default_amount}
                onChange={(e) => setForm({ ...form, default_amount: e.target.value })}
              />
            </Col>
            <Col xs={12}>
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.item_note}
                onChange={(e) => setForm({ ...form, item_note: e.target.value })}
              />
            </Col>
            <Col xs={12}>
              <Form.Check
                type="switch"
                label="เปิดใช้งานรายการนี้"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default FeeItems;
